import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Chip from '@material-ui/core/Chip';
import CircularProgress from '@material-ui/core/CircularProgress';
import Link from '@material-ui/core/Link';
import { alpha, useTheme } from '@material-ui/core/styles';
import LiveTvIcon from '@material-ui/icons/LiveTv';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import { fetchOndemandActiveEnabledByGenre, getShowBySlug, synopsisToBriefLine } from 'services/showService';
import {
  programGridByNiche,
  mergeProgramGridWithApiGroups,
} from '../../views/landingPages/TNOBusinessLevel/businessLevelPresentationData';
import { getNicheVisualTheme, nicheSectionId } from './nicheVisualTheme';
import NicheIllustration from './NicheIllustration';

const TNO_PUBLIC_ORIGIN = (
  (typeof process !== 'undefined' && process.env && process.env.REACT_APP_TNO_SITE_URL) ||
  'https://tnonetwork.com'
).replace(/\/$/, '');

const STRATEGIC_NEON_CYAN = '#22d3ee';

const DEFAULT_INTRO = null;

function useProgramGridCatalog() {
  const [apiCatalog, setApiCatalog] = useState({ status: 'idle', groups: [] });
  const [ondemandSynopsisBySlug, setOndemandSynopsisBySlug] = useState({});

  useEffect(() => {
    let cancelled = false;
    setApiCatalog({ status: 'loading', groups: [] });
    fetchOndemandActiveEnabledByGenre().then((res) => {
      if (cancelled) return;
      if (res.ok && Array.isArray(res.groups)) {
        setApiCatalog({
          status: res.groups.length > 0 ? 'ready' : 'empty',
          groups: res.groups,
        });
      } else {
        setApiCatalog({ status: 'error', groups: [] });
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (apiCatalog.status !== 'ready' || !apiCatalog.groups?.length) {
      setOndemandSynopsisBySlug({});
      return undefined;
    }
    let cancelled = false;
    const slugs = [];
    const seen = new Set();
    apiCatalog.groups.forEach((g) => {
      (g.shows || []).forEach((s) => {
        const slug = String(s.showSlug || '').trim();
        if (slug && !seen.has(slug)) {
          seen.add(slug);
          slugs.push(slug);
        }
      });
    });
    (async () => {
      const next = {};
      for (const slug of slugs) {
        if (cancelled) return;
        const detail = await getShowBySlug(slug);
        if (cancelled || !detail) continue;
        const raw = detail.synopsis || detail.description || detail.shortDescription;
        const line = synopsisToBriefLine(raw);
        if (line) next[slug] = line;
      }
      if (!cancelled) setOndemandSynopsisBySlug(next);
    })();
    return () => {
      cancelled = true;
    };
  }, [apiCatalog.status, apiCatalog.groups]);

  const mergedProgramGrid = useMemo(() => {
    const merged = mergeProgramGridWithApiGroups(
      programGridByNiche,
      apiCatalog.status === 'ready' ? apiCatalog.groups : [],
      TNO_PUBLIC_ORIGIN
    );
    return merged.filter((g) => (g.programs?.length || 0) > 0);
  }, [apiCatalog.status, apiCatalog.groups]);

  return { apiCatalog, mergedProgramGrid, ondemandSynopsisBySlug };
}

function ProgramCard({ program, nicheTheme, isDark, synopsisLine }) {
  const cardBg = isDark ? 'rgba(255,255,255,0.06)' : '#fff';
  const inner = (
    <>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, mb: 0.75 }}>
        <Typography
          sx={{
            fontWeight: 800,
            fontSize: '1.05rem',
            lineHeight: 1.3,
            color: isDark ? '#fff' : 'text.primary',
          }}
        >
          {program.name}
        </Typography>
        {program.href ? (
          <OpenInNewIcon sx={{ fontSize: 18, color: nicheTheme.main, opacity: 0.7, flexShrink: 0, mt: 0.25 }} />
        ) : null}
      </Box>
      <Chip
        size="small"
        label={program.note}
        sx={{
          height: 'auto',
          py: 0.5,
          mb: synopsisLine ? 1 : 0,
          bgcolor: alpha(nicheTheme.main, isDark ? 0.2 : 0.1),
          color: isDark ? '#fff' : nicheTheme.main,
          fontWeight: 600,
          fontSize: '0.78rem',
          '& .MuiChip-label': { whiteSpace: 'normal', lineHeight: 1.35 },
        }}
      />
      {synopsisLine ? (
        <Typography
          sx={{
            color: isDark ? 'rgba(255,255,255,0.65)' : 'text.secondary',
            fontSize: '0.84rem',
            lineHeight: 1.45,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {synopsisLine}
        </Typography>
      ) : null}
    </>
  );

  const cardSx = {
    height: '100%',
    p: 2,
    borderRadius: 2.5,
    bgcolor: cardBg,
    border: `1px solid ${alpha(nicheTheme.main, isDark ? 0.35 : 0.22)}`,
    borderLeft: `4px solid ${nicheTheme.main}`,
    boxSizing: 'border-box',
    boxShadow: isDark ? 'none' : `0 8px 24px ${alpha(nicheTheme.main, 0.08)}`,
    transition: 'transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease',
  };

  if (program.href) {
    return (
      <Link
        href={program.href}
        target="_blank"
        rel="noopener noreferrer"
        underline="none"
        sx={{
          ...cardSx,
          display: 'block',
          color: 'inherit',
          '&:hover': {
            transform: 'translateY(-3px)',
            boxShadow: `0 14px 32px ${alpha(nicheTheme.main, 0.18)}`,
            borderColor: nicheTheme.accent,
          },
        }}
      >
        {inner}
      </Link>
    );
  }

  return <Box sx={cardSx}>{inner}</Box>;
}

ProgramCard.propTypes = {
  program: PropTypes.object.isRequired,
  nicheTheme: PropTypes.object.isRequired,
  isDark: PropTypes.bool,
  synopsisLine: PropTypes.string,
};

function RichNicheSection({ group, isDark, ondemandSynopsisBySlug }) {
  const theme = getNicheVisualTheme(group.niche);
  const sectionId = `niche-${nicheSectionId(group.niche)}`;
  const count = group.programs?.length || 0;

  return (
    <Box
      id={sectionId}
      component="section"
      sx={{
        mb: { xs: 4, md: 5 },
        borderRadius: { xs: 3, md: 4 },
        overflow: 'hidden',
        boxShadow: isDark ? '0 12px 40px rgba(0,0,0,0.35)' : '0 16px 48px rgba(15,23,42,0.08)',
        border: isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(15,23,42,0.06)',
      }}
    >
      <Box
        sx={{
          background: theme.gradient,
          px: { xs: 2, md: 3 },
          py: { xs: 2.5, md: 3 },
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'flex-start', sm: 'center' },
          gap: 2,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            width: 180,
            height: 180,
            borderRadius: '50%',
            top: -70,
            right: -30,
            bgcolor: 'rgba(255,255,255,0.12)',
            pointerEvents: 'none',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            width: 100,
            height: 100,
            borderRadius: '50%',
            bottom: -40,
            left: '18%',
            bgcolor: 'rgba(255,255,255,0.08)',
            pointerEvents: 'none',
          }}
        />
        <NicheIllustration type={theme.illustration} width={110} color="#ffffff" />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Chip
            label={`${count} ${count === 1 ? 'producción' : 'producciones'}`}
            size="small"
            sx={{
              mb: 1,
              bgcolor: 'rgba(255,255,255,0.22)',
              color: '#fff',
              fontWeight: 700,
              backdropFilter: 'blur(6px)',
            }}
          />
          <Typography
            component="h3"
            sx={{
              fontWeight: 900,
              fontSize: { xs: '1.25rem', md: '1.55rem' },
              color: '#fff',
              lineHeight: 1.15,
              textTransform: 'uppercase',
              letterSpacing: 0.5,
            }}
          >
            {group.niche}
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.88)', fontSize: '0.95rem', mt: 0.75, maxWidth: 520 }}>
            {theme.tagline}
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          px: { xs: 1.5, md: 2.5 },
          py: { xs: 2, md: 2.5 },
          bgcolor: isDark ? 'rgba(8,12,28,0.6)' : theme.light,
          position: 'relative',
          backgroundImage: isDark
            ? 'none'
            : `radial-gradient(circle at 1px 1px, ${alpha(theme.main, 0.07)} 1px, transparent 0)`,
          backgroundSize: '20px 20px',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            right: { xs: 8, md: 16 },
            bottom: { xs: 8, md: 12 },
            opacity: 0.12,
            pointerEvents: 'none',
            display: { xs: 'none', sm: 'block' },
          }}
        >
          <NicheIllustration type={theme.illustration} width={88} color={theme.main} />
        </Box>
        <Grid container spacing={2}>
          {group.programs.map((p) => (
            <Grid item xs={12} sm={6} lg={4} key={`${group.niche}-${p.name}`}>
              <ProgramCard
                program={p}
                nicheTheme={theme}
                isDark={isDark}
                synopsisLine={p.showSlug ? ondemandSynopsisBySlug[p.showSlug] : null}
              />
            </Grid>
          ))}
        </Grid>
      </Box>
    </Box>
  );
}

RichNicheSection.propTypes = {
  group: PropTypes.object.isRequired,
  isDark: PropTypes.bool,
  ondemandSynopsisBySlug: PropTypes.object,
};

function NicheQuickNav({ groups, isDark }) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 1,
        justifyContent: 'center',
        mb: 4,
        px: 0.5,
      }}
    >
      {groups.map((g) => {
        const theme = getNicheVisualTheme(g.niche);
        return (
          <Chip
            key={g.niche}
            component="a"
            href={`#niche-${nicheSectionId(g.niche)}`}
            clickable
            label={g.niche}
            sx={{
              fontWeight: 600,
              fontSize: '0.8rem',
              bgcolor: isDark ? alpha(theme.main, 0.35) : theme.light,
              color: isDark ? '#fff' : theme.main,
              border: `1px solid ${alpha(theme.main, 0.35)}`,
              maxWidth: { xs: '100%', sm: 280 },
              height: 'auto',
              py: 0.75,
              '& .MuiChip-label': { whiteSpace: 'normal', lineHeight: 1.3 },
              '&:hover': {
                bgcolor: theme.main,
                color: '#fff',
              },
            }}
          />
        );
      })}
    </Box>
  );
}

NicheQuickNav.propTypes = {
  groups: PropTypes.array.isRequired,
  isDark: PropTypes.bool,
};

const ProgramGridByNiche = ({
  variant = 'light',
  showHeader = true,
  richLayout = false,
  showQuickNav = false,
  overline = 'Parrilla TNO Network',
  title = 'Grilla por nichos',
  intro = DEFAULT_INTRO,
  sx = {},
}) => {
  const theme = useTheme();
  const isDark = variant === 'dark';
  const { apiCatalog, mergedProgramGrid, ondemandSynopsisBySlug } = useProgramGridCatalog();

  const headingColor = isDark ? 'common.white' : 'text.primary';
  const bodyMuted = isDark ? 'rgba(255,255,255,0.75)' : 'text.secondary';
  const introBoxBg = isDark ? 'rgba(16, 20, 48, 0.96)' : alpha(theme.palette.primary.main, 0.05);
  const introBoxBorder = isDark
    ? alpha(theme.palette.primary.main, 0.25)
    : alpha(theme.palette.primary.main, 0.15);

  if (richLayout) {
    return (
      <Box sx={sx}>
        {showHeader ? (
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <LiveTvIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
            <Typography variant="overline" sx={{ color: 'secondary.main', letterSpacing: 3, fontWeight: 800 }}>
              {overline}
            </Typography>
            <Typography
              component="h2"
              sx={{
                fontWeight: 900,
                fontSize: { xs: '1.85rem', md: '2.5rem' },
                color: headingColor,
                mt: 1,
              }}
            >
              {title}
            </Typography>
          </Box>
        ) : null}

        {apiCatalog.status === 'loading' && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mb: 3 }}>
            <CircularProgress size={28} sx={{ color: isDark ? STRATEGIC_NEON_CYAN : 'primary.main' }} />
            <Typography sx={{ color: bodyMuted, fontSize: '0.95rem' }}>
              Incorporando producciones desde el servicio de shows…
            </Typography>
          </Box>
        )}

        {intro ? (
          <Box
            sx={{
              mb: 4,
              p: { xs: 2, md: 2.75 },
              borderRadius: 3,
              bgcolor: introBoxBg,
              border: '1px solid',
              borderColor: introBoxBorder,
              maxWidth: 920,
              mx: 'auto',
              textAlign: 'center',
              backgroundImage: isDark
                ? 'none'
                : `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.04)} 0%, ${alpha(theme.palette.secondary.main, 0.08)} 100%)`,
            }}
          >
            <Typography sx={{ color: isDark ? 'rgba(255,255,255,0.9)' : 'text.primary', fontSize: '1rem', lineHeight: 1.7 }}>
              {intro}
            </Typography>
          </Box>
        ) : null}

        {showQuickNav && mergedProgramGrid.length > 0 ? (
          <NicheQuickNav groups={mergedProgramGrid} isDark={isDark} />
        ) : null}

        {mergedProgramGrid.map((group) => (
          <RichNicheSection
            key={group.niche}
            group={group}
            isDark={isDark}
            ondemandSynopsisBySlug={ondemandSynopsisBySlug}
          />
        ))}
      </Box>
    );
  }

  return (
    <Box sx={sx}>
      {showHeader ? (
        <>
          <LiveTvIcon sx={{ fontSize: 44, color: 'primary.main', mb: 1, display: 'block', mx: 'auto' }} />
          <Typography
            variant="overline"
            sx={{ color: 'primary.main', letterSpacing: 4, fontWeight: 800, mb: 1, display: 'block', textAlign: 'center' }}
          >
            {overline}
          </Typography>
          <Typography
            component="h2"
            sx={{
              fontWeight: 800,
              fontSize: { xs: '1.72rem', md: '2.35rem' },
              color: headingColor,
              mb: 2,
              textAlign: 'center',
            }}
          >
            {title}
          </Typography>
        </>
      ) : null}

      {apiCatalog.status === 'loading' && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mb: 3 }}>
          <CircularProgress size={28} sx={{ color: isDark ? STRATEGIC_NEON_CYAN : 'primary.main' }} />
          <Typography sx={{ color: bodyMuted, fontSize: '0.95rem' }}>
            Incorporando producciones desde el servicio de shows…
          </Typography>
        </Box>
      )}

      {intro ? (
        <Box
          sx={{
            mb: 4,
            p: { xs: 2, md: 2.5 },
            borderRadius: 2,
            bgcolor: introBoxBg,
            border: '1px solid',
            borderColor: introBoxBorder,
            maxWidth: 900,
            mx: 'auto',
            textAlign: 'center',
          }}
        >
          <Typography sx={{ color: isDark ? 'rgba(255,255,255,0.88)' : 'text.primary', fontSize: '0.98rem', lineHeight: 1.65 }}>
            {intro}
          </Typography>
        </Box>
      ) : null}

      {mergedProgramGrid.map((group) => {
        const nicheTheme = getNicheVisualTheme(group.niche);
        return (
          <Box key={group.niche} sx={{ width: '100%', mb: 4 }}>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: { xs: '1.12rem', md: '1.22rem' },
                color: nicheTheme.main,
                textTransform: 'uppercase',
                letterSpacing: 1.2,
                mb: 1.5,
                borderLeft: `4px solid ${nicheTheme.main}`,
                pl: 1.5,
              }}
            >
              {group.niche}
            </Typography>
            <Grid container spacing={1.5}>
              {group.programs.map((p) => (
                <Grid item xs={12} sm={6} md={4} key={`${group.niche}-${p.name}`}>
                  <ProgramCard
                    program={p}
                    nicheTheme={nicheTheme}
                    isDark={isDark}
                    synopsisLine={p.showSlug ? ondemandSynopsisBySlug[p.showSlug] : null}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        );
      })}
    </Box>
  );
};

ProgramGridByNiche.propTypes = {
  variant: PropTypes.oneOf(['light', 'dark']),
  showHeader: PropTypes.bool,
  richLayout: PropTypes.bool,
  showQuickNav: PropTypes.bool,
  overline: PropTypes.string,
  title: PropTypes.string,
  intro: PropTypes.node,
  sx: PropTypes.object,
};

export default ProgramGridByNiche;
